import {EvalEnv, VariableMap} from '@packages/systems/wfdl';
import {
  pageVariable,
  type PageContext,
} from '@packages/shared/constants/Render';
import {weakMemo, singleMemo} from '@packages/utilities/memo';
import {IMAGE_PLACEHOLDER_FULL_ID} from '@packages/systems/plugins-utils/Asset';
import type {CurrentPage} from './types';

function buildPageContext(currentPage: CurrentPage): PageContext {
  return {
    name: [currentPage.name],
    parentFolderName: [currentPage.parentFolderName?.trim() ?? ''],
    seoTitle: [currentPage.seoTitle?.trim() ?? ''],
    seoDesc: [currentPage.seoDesc?.trim() ?? ''],
    ogTitle: [currentPage.ogTitle?.trim() ?? ''],
    ogDesc: [currentPage.ogDesc?.trim() ?? ''],
    ogImage: currentPage.ogImage ?? {id: IMAGE_PLACEHOLDER_FULL_ID},
  };
}

function ogImageEqual(
  a: PageContext['ogImage'],
  b: PageContext['ogImage']
): boolean {
  if ('id' in a && 'id' in b) {
    return a.id === b.id;
  }
  if ('url' in a && 'url' in b) {
    return a.url === b.url;
  }
  return false;
}

function pageContextEqual(a: PageContext, b: PageContext): boolean {
  return (
    a.name[0] === b.name[0] &&
    a.parentFolderName[0] === b.parentFolderName[0] &&
    a.seoTitle[0] === b.seoTitle[0] &&
    a.seoDesc[0] === b.seoDesc[0] &&
    a.ogTitle[0] === b.ogTitle[0] &&
    a.ogDesc[0] === b.ogDesc[0] &&
    ogImageEqual(a.ogImage, b.ogImage)
  );
}

/**
 * Inserts current page context into the evaluation environment.
 *
 * Takes a pre-resolved `CurrentPage` object (already accounting for locale
 * fallback and component-canvas homepage resolution) and injects it into
 * the eval env under the `page` variable. Optional fields are trimmed and
 * omitted from the `PageContext` when empty/whitespace.
 *
 * If the env already holds an equivalent `page` value, returns the same env
 * reference without inserting (avoids churn when bindings re-resolve to the
 * same strings/objects).
 */
export const updateEnvForCurrentPageSettings = weakMemo(
  (evalEnv: EvalEnv.EvalEnvType) =>
    singleMemo((currentPage: CurrentPage | null | undefined) => {
      if (!currentPage) return evalEnv;

      const nextContext = buildPageContext(currentPage);
      const existing = VariableMap.get(
        EvalEnv.getValues(evalEnv),
        pageVariable
      ) as PageContext | undefined;

      if (existing && pageContextEqual(existing, nextContext)) {
        return evalEnv;
      }

      return EvalEnv.insertValue(evalEnv, pageVariable, nextContext);
    })
);
